import React, { useMemo, useState } from "react";
import { useApiData } from "@/hooks/useApiData";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import RefreshButton from "@/components/RefreshButton";
import { downloadFile, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Truck, Search, AlertCircle, FileDown, Eye, MapPin, Loader2, FileText } from "lucide-react";
import { formatDate, wasteLabel, formatDateTime } from "@/lib/format";
import { toast } from "sonner";

export default function Collections() {
  const { data, loading, refreshing, error, refetch } = useApiData("/portal/jobs");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState("");

  const onRefresh = async () => {
    const ok = await refetch();
    if (ok) toast.success("Collections up to date");
  };

  const jobs = Array.isArray(data) ? data : [];

  const statuses = useMemo(() => {
    const s = new Set(jobs.map((j) => j.status).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [jobs]);

  const filtered = useMemo(() => {
    return jobs
      .filter((j) => (statusFilter === "all" ? true : j.status === statusFilter))
      .filter((j) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          (j.wtn_number || "").toLowerCase().includes(q) ||
          (j.hwcn_number || "").toLowerCase().includes(q) ||
          (j.waste_types || []).some((w) => wasteLabel(w).toLowerCase().includes(q)) ||
          formatDate(j.scheduled_date).toLowerCase().includes(q)
        );
      })
      .sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));
  }, [jobs, statusFilter, search]);

  const handleDownload = async (job, kind) => {
    const key = `${job.id}-${kind}`;
    setDownloading(key);
    try {
      await downloadFile({ upstreamUrl: `/api/jobs/${job.id}/${kind}.pdf`, filename: `${kind.toUpperCase()}-${job.scheduled_date}.pdf` });
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(extractError(e, "Download failed"));
    } finally {
      setDownloading("");
    }
  };

  return (
    <div data-testid="collections-page">
      <PageHeader title="Collection history" subtitle="Your scheduled and completed waste collections" actions={<RefreshButton refreshing={refreshing} onClick={onRefresh} testId="collections-refresh" />} />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search date, waste type, WTN…" className="h-10 pl-9" data-testid="collections-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 sm:w-48" data-testid="collections-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {statuses.map((s) => (
              <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <Card className="rounded-xl border bg-white p-4"><div className="space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div></Card>
      ) : error ? (
        <Alert variant="destructive" data-testid="collections-error"><AlertCircle className="h-4 w-4" /><AlertTitle>Couldn't load collections</AlertTitle><AlertDescription className="flex items-center gap-3">{error}<Button size="sm" variant="outline" onClick={refetch}>Retry</Button></AlertDescription></Alert>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Truck} title="No collections found" description={jobs.length ? "Try adjusting your search or filters." : "Your collection history will appear here once collections are scheduled."} testId="collections-empty" />
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden overflow-hidden rounded-xl border bg-white shadow-[var(--shadow-sm)] md:block">
            <Table data-testid="collections-table">
              <TableHeader>
                <TableRow className="bg-[var(--hh-band)] hover:bg-[var(--hh-band)]">
                  <TableHead>Date</TableHead>
                  <TableHead>Waste types</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>WTN / HWCN</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((job) => (
                  <TableRow key={job.id} className="hover:bg-[var(--hh-band)]" data-testid={`collection-row-${job.id}`}>
                    <TableCell className="whitespace-nowrap font-medium text-[color:var(--hh-ink)]">{formatDate(job.scheduled_date)}<div className="text-xs font-normal text-muted-foreground">{job.scheduled_time}</div></TableCell>
                    <TableCell><div className="flex flex-wrap gap-1">{(job.waste_types || []).map((w) => <span key={w} className="rounded-full border border-[color:var(--hh-line)] bg-[var(--hh-band)] px-2 py-0.5 text-xs font-medium text-[color:var(--hh-slate)]">{wasteLabel(w)}</span>)}</div></TableCell>
                    <TableCell><StatusBadge status={job.status} /></TableCell>
                    <TableCell className="text-sm text-[color:var(--hh-slate)]">{job.wtn_number || "\u2014"}{job.hwcn_number ? <div className="text-xs text-muted-foreground">{job.hwcn_number}</div> : null}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1.5">
                        {job.wtn_number && (
                          <Button size="sm" variant="outline" className="h-8" disabled={downloading === `${job.id}-wtn`} onClick={() => handleDownload(job, "wtn")} data-testid={`download-wtn-${job.id}`}>
                            {downloading === `${job.id}-wtn` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}<span className="ml-1">WTN</span>
                          </Button>
                        )}
                        {job.hwcn_number && (
                          <Button size="sm" variant="outline" className="h-8" disabled={downloading === `${job.id}-hwcn`} onClick={() => handleDownload(job, "hwcn")} data-testid={`download-hwcn-${job.id}`}>
                            {downloading === `${job.id}-hwcn` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}<span className="ml-1">HWCN</span>
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setSelected(job)} data-testid={`view-collection-${job.id}`}><Eye className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {filtered.map((job) => (
              <Card key={job.id} className="rounded-xl border bg-white p-4 shadow-[var(--shadow-sm)]" data-testid={`collection-card-${job.id}`}>
                <div className="flex items-start justify-between">
                  <div><div className="font-medium text-[color:var(--hh-ink)]">{formatDate(job.scheduled_date)}</div><div className="text-xs text-muted-foreground">{job.scheduled_time}</div></div>
                  <StatusBadge status={job.status} />
                </div>
                <div className="mt-3 flex flex-wrap gap-1">{(job.waste_types || []).map((w) => <span key={w} className="rounded-full border border-[color:var(--hh-line)] bg-[var(--hh-band)] px-2 py-0.5 text-xs font-medium text-[color:var(--hh-slate)]">{wasteLabel(w)}</span>)}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {job.wtn_number && <Button size="sm" variant="outline" className="h-8" onClick={() => handleDownload(job, "wtn")}><FileDown className="mr-1 h-3.5 w-3.5" /> WTN</Button>}
                  {job.hwcn_number && <Button size="sm" variant="outline" className="h-8" onClick={() => handleDownload(job, "hwcn")}><FileDown className="mr-1 h-3.5 w-3.5" /> HWCN</Button>}
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setSelected(job)}><Eye className="mr-1 h-4 w-4" /> Details</Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="collection-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">Collection details</DialogTitle><DialogDescription className="sr-only">Details of this collection including date, waste types and documents.</DialogDescription></DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center justify-between">
                <div><div className="font-display text-xl font-semibold text-[color:var(--hh-ink)]">{formatDate(selected.scheduled_date, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div><div className="text-muted-foreground">{selected.scheduled_time}</div></div>
                <StatusBadge status={selected.status} />
              </div>
              <div className="flex items-start gap-2 text-[color:var(--hh-slate)]"><MapPin className="mt-0.5 h-4 w-4 text-[color:var(--hh-amber-600)]" />{[selected.address, selected.postcode].filter(Boolean).join(", ")}</div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-[color:var(--hh-slate)]">Waste types</div>
                <div className="mt-2 flex flex-wrap gap-1">{(selected.waste_types || []).map((w) => <span key={w} className="rounded-full border border-[color:var(--hh-line)] bg-[var(--hh-band)] px-2 py-0.5 text-xs font-medium text-[color:var(--hh-slate)]">{wasteLabel(w)}</span>)}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 border-t border-[color:var(--hh-line)] pt-4">
                <div><div className="text-xs text-muted-foreground">WTN number</div><div className="font-medium text-[color:var(--hh-ink)]">{selected.wtn_number || "\u2014"}</div></div>
                <div><div className="text-xs text-muted-foreground">HWCN number</div><div className="font-medium text-[color:var(--hh-ink)]">{selected.hwcn_number || "\u2014"}</div></div>
                <div><div className="text-xs text-muted-foreground">Driver</div><div className="font-medium text-[color:var(--hh-ink)]">{selected.driver_name || "\u2014"}</div></div>
                <div><div className="text-xs text-muted-foreground">Completed</div><div className="font-medium text-[color:var(--hh-ink)]">{selected.completed_at ? formatDateTime(selected.completed_at) : "\u2014"}</div></div>
              </div>
              {selected.completion_notes && <div className="rounded-lg bg-[var(--hh-band)] p-3"><div className="text-xs font-semibold text-[color:var(--hh-slate)]">Notes</div><div className="mt-1 text-[color:var(--hh-ink)]">{selected.completion_notes}</div></div>}
              <div className="flex flex-wrap gap-2 pt-2">
                {selected.wtn_number && <Button variant="outline" className="rounded-xl" onClick={() => handleDownload(selected, "wtn")}><FileText className="mr-1.5 h-4 w-4" /> Download WTN</Button>}
                {selected.hwcn_number && <Button variant="outline" className="rounded-xl" onClick={() => handleDownload(selected, "hwcn")}><FileText className="mr-1.5 h-4 w-4" /> Download HWCN</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
